import { useEffect, useRef, useState } from 'react';
import { Icon } from './Icon.js';
import { Sheet } from './Sheet.js';
import { api, type NewTransaction } from '../lib/api.js';
import { useApp } from '../store/app.js';
import { t } from '../lib/i18n.js';

type Mode = 'expense' | 'income' | 'transfer';

/**
 * Adding an expense by hand must take under 5 seconds. §7
 * Tap opens the form with the amount focused. Long press opens the extended menu.
 */
export function QuickAdd() {
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const timer = useRef<number>();

  const startPress = () => {
    timer.current = window.setTimeout(() => { setMenu(true); navigator.vibrate?.(8); }, 420);
  };
  const endPress = (fire: boolean) => {
    if (timer.current) window.clearTimeout(timer.current);
    if (fire && !menu) setOpen(true);
  };

  return (
    <>
      <button
        className="fab"
        aria-label="הוספה מהירה"
        onPointerDown={startPress}
        onPointerUp={() => endPress(true)}
        onPointerLeave={() => endPress(false)}
        onContextMenu={(e) => { e.preventDefault(); setMenu(true); }}
      >
        <Icon name="plus" size={26} />
      </button>

      <Sheet open={menu} title="מה להוסיף?" onClose={() => setMenu(false)}>
        <div className="stack">
          {[
            { icon: 'camera', label: 'סריקת קבלה', hint: 'צילום והמערכת תמלא את הפרטים' },
            { icon: 'arrow-up', label: 'הוצאה', hint: 'חיוב חדש' },
            { icon: 'arrow-down', label: 'הכנסה', hint: 'כסף שנכנס' },
            { icon: 'transfer', label: 'העברה', hint: 'בין חשבונות שלך' },
            { icon: 'repeat', label: 'חיוב קבוע', hint: 'מנוי או תשלום חוזר' },
          ].map((item) => (
            <button
              key={item.label}
              className="tx-row"
              onClick={() => { setMenu(false); setOpen(true); }}
            >
              <span className="tx-row__icon" style={{ background: 'var(--primary-soft)', color: 'var(--primary)' }}>
                <Icon name={item.icon as never} size={19} />
              </span>
              <span className="tx-row__body">
                <span className="tx-row__name">{item.label}</span>
                <span className="tx-row__meta">{item.hint}</span>
              </span>
            </button>
          ))}
        </div>
      </Sheet>

      <QuickAddForm open={open} onClose={() => setOpen(false)} />
    </>
  );
}

function QuickAddForm({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { accounts, categories, refreshDashboard, refreshAccounts, toast } = useApp();
  const [mode, setMode] = useState<Mode>('expense');
  const [amount, setAmount] = useState('');
  const [merchant, setMerchant] = useState('');
  const [accountId, setAccountId] = useState('');
  const [toAccountId, setToAccountId] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const amountRef = useRef<HTMLInputElement>(null);

  const spendable = accounts.filter((a) => !a.archived);
  const quickCategories = categories
    .filter((c) => c.kind === (mode === 'income' ? 'income' : 'expense') && c.system)
    .slice(0, 10);

  useEffect(() => {
    if (!open) return;
    setAmount(''); setMerchant(''); setCategoryId(null); setMode('expense');
    setAccountId(spendable.find((a) => a.isSpendable)?.id ?? spendable[0]?.id ?? '');
    setToAccountId(spendable.find((a) => a.type === 'savings')?.id ?? '');
    setTimeout(() => amountRef.current?.focus(), 80);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = async () => {
    const minor = Math.round(Number(amount.replace(',', '.')) * 100);
    if (!Number.isFinite(minor) || minor <= 0) { toast('צריך סכום גדול מאפס', { tone: 'danger' }); return; }
    if (!accountId) { toast('בחר חשבון', { tone: 'danger' }); return; }

    const payload: NewTransaction = {
      type: mode,
      accountId,
      amount: minor,
      merchant: merchant.trim() || null,
      categoryId: mode === 'transfer' ? null : categoryId,
      ...(mode === 'transfer' ? { toAccountId } : {}),
    };

    setSaving(true);
    try {
      const created = await api.transactions.create(payload);
      onClose();
      toast(`${t.types[mode]} נשמרה`, {
        tone: 'positive',
        action: {
          label: t.common.undo,
          run: () => {
            void api.transactions.remove(created.id).then(() => {
              void refreshDashboard(); void refreshAccounts();
            });
          },
        },
      });
      await Promise.all([refreshDashboard(), refreshAccounts()]);
    } catch {
      toast(t.errors.save, { tone: 'danger' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} title="הוספה מהירה" onClose={onClose}>
      <div className="stack">
        <div className="segmented" role="group" aria-label="סוג תנועה">
          {(['expense', 'income', 'transfer'] as Mode[]).map((value) => (
            <button key={value} aria-pressed={mode === value} onClick={() => setMode(value)}>
              {t.types[value]}
            </button>
          ))}
        </div>

        <label className="field">
          <span className="sr-only">סכום</span>
          <input
            ref={amountRef}
            className="input input--amount"
            inputMode="decimal"
            placeholder="0"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.,]/g, ''))}
            aria-label="סכום"
          />
        </label>

        {mode !== 'transfer' && (
          <label className="field">
            <span className="field__label">בית עסק</span>
            <input className="input" value={merchant} onChange={(e) => setMerchant(e.target.value)} placeholder="לדוגמה: וולט" />
          </label>
        )}

        <label className="field">
          <span className="field__label">{mode === 'transfer' ? 'מחשבון' : 'חשבון'}</span>
          <select className="select" value={accountId} onChange={(e) => setAccountId(e.target.value)}>
            {spendable.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </label>

        {mode === 'transfer' && (
          <label className="field">
            <span className="field__label">לחשבון</span>
            <select className="select" value={toAccountId} onChange={(e) => setToAccountId(e.target.value)}>
              {spendable.filter((a) => a.id !== accountId).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </label>
        )}

        {mode !== 'transfer' && (
          <div className="field">
            <span className="field__label">קטגוריה</span>
            <div className="chip-row" style={{ padding: 0 }}>
              {quickCategories.map((c) => (
                <button
                  key={c.id}
                  className="chip"
                  aria-pressed={categoryId === c.id}
                  onClick={() => setCategoryId(categoryId === c.id ? null : c.id)}
                >
                  {c.nameHe ?? c.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {mode === 'transfer' && (
          <p className="label">העברה בין החשבונות שלך אינה הוצאה ולא תיספר בניתוח ההוצאות.</p>
        )}

        <button className="btn btn--primary btn--block" onClick={submit} disabled={saving}>
          {saving ? 'שומר…' : t.common.save}
        </button>
      </div>
    </Sheet>
  );
}
